import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Icon from "@mdi/react";
import {
    mdiMapMarker,
    mdiPencil,
    mdiStar,
    mdiEye,
    mdiEyeOff,
    mdiTrashCan,
    mdiPlus,
    mdiShareVariant,
    mdiContentCopy,
    mdiQrcode,
    mdiDownload,
    mdiInformationOutline,
    mdiOpenInNew,
    mdiHomeOutline,
    mdiAirplaneTakeoff,
    mdiChevronDown,
    mdiChevronRight,
    mdiContentSaveCogOutline,
    mdiInstagram,
    mdiFacebook,
    mdiWeb,
} from "@mdi/js";
import QRCode from "qrcode";
import { instructorApi } from "../../api/instructor";
import { bookingApi } from "../../api/booking";
import { useAuth } from "../../contexts/AuthContext";
import AppHeader from "../../components/AppHeader";
import TeachingLocationModal from "../../components/TeachingLocationModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import CustomFormInput from "../../components/forms/CustomFormInput";
import webApiClient from "../../api/client";
import InstructorAvatarUpload from "../../components/shared/InstructorAvatarUpload";
import InstructorCoverUpload from "../../components/shared/InstructorCoverUpload";
import InstructorCoverPresets from "../../components/shared/InstructorCoverPresets";
import { INSTRUCTOR_COVER_PRESETS } from "../../assets/instructorCoverPresets";
import InstructorAvatarPresets from "../../components/shared/InstructorAvatarPresets";
import { INSTRUCTOR_AVATAR_PRESETS } from "../../assets/instructorAvatarPresets";
import useInstructorProfile from "../../hooks/useInstructorProfile";
import InstructorCard from "../../components/InstructorCard";
import { organizationApi } from "../../api/organization";
import { SUPPLIER_POLICY_EDITOR_FIELDS } from "../../components/SupplierPolicyAcknowledgement";
import coverPlaceholderImg from "../../assets/instructor_default_cover.jpg";
import { cacheImage, getCachedImage, base64ToBlobUrl } from "../../utils/imageCache";
import DanceStyleChips from "../../components/DanceStyleChips";
import { formatCurrency } from "../../utils/currency";
import "./InstructorProfileSetup.css";
import "../Common.css";
import "../Dashboard.css";
import "../header.css";
/**
 * InstructorProfilePage - Instructor Personal Profile Settings
 *
 * Extracted from InstructorProfileSetup.js (lines 648-1003)
 * (Original monolithic file deleted in 3.2.66 refactor)
 * Renamed from PersonalDetailsPage.js (2026-02-16)
 *
 * SCOPE: Instructor-level personal profile settings (NOT organization/business settings)
 * - Profile sharing (web URL, deep link for student acquisition)
 * - Profile Card (teaching name, dance style specialties, hourly rate)
 * - Bio and qualifications (personal teaching philosophy)
 * - Private booking defaults (approval mode, payment timing)
 * - Teaching locations and timezone (where instructor teaches)
 *
 * DISTINCTION: This is for individual instructor settings.
 * Organization/business settings (org name, business location, logo, business blurb)
 * belong in Settings.js - see DL-15FEB26-01 for business settings implementation.
 */
const InstructorProfilePage = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const {
        profileData,
        isLoading,
        isSaving,
        error,
        successMessage,
        hasExistingProfile,
        saveProfile,
        validateProfile,
        normaliseUrl,
        handleInputChange,
        loadProfile,
        setError,
        setSuccessMessage,
    } = useInstructorProfile();

    // Local UI state
    const [showLocationPicker, setShowLocationPicker] = useState(false);
    const [editingLocation, setEditingLocation] = useState(null);

    // Settings accordion state — mirrors the org Settings page pattern
    // (exclusive-open: opening one section closes the others).
    const [accordion, setAccordion] = useState({
        profileCard: true,
        about: false,
        share: false,
        bookingDefaults: false,
        terms: false,
        locations: false,
    });
    const toggleAccordion = sectionKey => {
        setAccordion(prev => {
            const allClosed = Object.keys(prev).reduce((acc, k) => ({ ...acc, [k]: false }), {});
            return { ...allClosed, [sectionKey]: !prev[sectionKey] };
        });
    };

    // Per-field "Added https:// for you" hint, shown briefly after a blur correction.
    const [correctedLinks, setCorrectedLinks] = useState({});
    const handleLinkBlur = (name, value) => {
        const result = normaliseUrl(value);
        if (result !== null && result !== value) {
            handleInputChange({ target: { name, value: result } });
            setCorrectedLinks(prev => ({ ...prev, [name]: true }));
            setTimeout(() => setCorrectedLinks(prev => ({ ...prev, [name]: false })), 3000);
        }
    };

    // Share profile state
    const [qrCodeUrl, setQrCodeUrl] = useState("");
    const [showQRCode, setShowQRCode] = useState(false);
    const [copySuccess, setCopySuccess] = useState(false);
    const [shareData, setShareData] = useState(null); // API share data (webUrl, deepLink, shareText)

    // Delete location confirmation modal state
    const [showDeleteLocationModal, setShowDeleteLocationModal] = useState(false);
    const [locationToDelete, setLocationToDelete] = useState(null);

    // Collapsible non-default location cards
    const [expandedLocationIds, setExpandedLocationIds] = useState([]);
    const toggleLocationExpanded = locationId => {
        setExpandedLocationIds(prev =>
            prev.includes(locationId) ? prev.filter(id => id !== locationId) : [...prev, locationId]
        );
    };

    // Profile visibility toggle state
    const [isProfileVisible, setIsProfileVisible] = useState(true); // accepting bookings (booking CTA)
    const [isProfileSearchable, setIsProfileSearchable] = useState(true); // appears in instructor search/directory

    // Instructor profile photo + cover image state
    const [instructorPhotoUrl, setInstructorPhotoUrl] = useState(null);
    const [photoPreviewUrl, setPhotoPreviewUrl] = useState(null);
    const [instructorCoverImageUrl, setInstructorCoverImageUrl] = useState(null);
    const [coverPreviewUrl, setCoverPreviewUrl] = useState(null);
    const coverPlaceholderUrl = coverPlaceholderImg;
    const [orgCoverImageKey, setOrgCoverImageKey] = useState(null);
    const [orgCurrency, setOrgCurrency] = useState("AUD");
    const [isApplyingOrgCover, setIsApplyingOrgCover] = useState(false);
    const [selectedPresetId, setSelectedPresetId] = useState(null);
    const [applyingPresetId, setApplyingPresetId] = useState(null);
    const [pendingPresetBase64, setPendingPresetBase64] = useState(null);
    const [selectedAvatarPresetId, setSelectedAvatarPresetId] = useState(null);
    const [applyingAvatarPresetId, setApplyingAvatarPresetId] = useState(null);
    const [pendingAvatarPresetBase64, setPendingAvatarPresetBase64] = useState(null);

    // Private booking defaults state (approval mode + payment timing)
    const [approvalMode, setApprovalMode] = useState("existing-students");
    const [paymentTiming, setPaymentTiming] = useState("after-lesson");
    const [isSavingBookingSettings, setIsSavingBookingSettings] = useState(false);
    // Private-lesson terms and conditions (buyer-facing; shown at booking checkout).
    const [privatePolicy, setPrivatePolicy] = useState(null);
    const [isSavingTerms, setIsSavingTerms] = useState(false);

    const timezoneOptions = [
        { value: "Australia/Sydney", label: "Sydney (GMT+11:00)", utcOffset: "+11:00" },
        { value: "Australia/Melbourne", label: "Melbourne (GMT+11:00)", utcOffset: "+11:00" },
        { value: "Australia/Brisbane", label: "Brisbane (GMT+10:00)", utcOffset: "+10:00" },
        { value: "Australia/Perth", label: "Perth (GMT+08:00)", utcOffset: "+08:00" },
        { value: "Pacific/Auckland", label: "Auckland (GMT+13:00)", utcOffset: "+13:00" },
        { value: "America/Los_Angeles", label: "Los Angeles (GMT-08:00)", utcOffset: "-08:00" },
        { value: "America/New_York", label: "New York (GMT-05:00)", utcOffset: "-05:00" },
        { value: "Europe/London", label: "London (GMT+00:00)", utcOffset: "+00:00" },
        { value: "Europe/Paris", label: "Paris (GMT+01:00)", utcOffset: "+01:00" },
        { value: "Asia/Tokyo", label: "Tokyo (GMT+09:00)", utcOffset: "+09:00" },
        { value: "America/Sao_Paulo", label: "São Paulo (GMT-03:00)", utcOffset: "-03:00" },
    ];

    // Show toast notifications for success/error messages
    useEffect(() => {
        if (successMessage) {
            toast.success(successMessage);
        }
        if (error) {
            toast.error(error);
        }
    }, [successMessage, error]);

    // Fetch share data when profile loads
    useEffect(() => {
        if (hasExistingProfile && user?.token) {
            fetchShareData();
        }
    }, [hasExistingProfile, user?.token]);

    // Load profile visibility (accepting bookings) + searchability from profileData
    useEffect(() => {
        if (profileData?.isActive !== undefined) {
            setIsProfileVisible(profileData.isActive);
        }
    }, [profileData?.isActive]);

    useEffect(() => {
        if (profileData?.isSearchable !== undefined) {
            setIsProfileSearchable(profileData.isSearchable);
        }
    }, [profileData?.isSearchable]);

    // Load org cover key (for "use school cover") + org base currency (for venue-hire display
    // in the location picker — base currency lives on the org, not the instructor profile).
    useEffect(() => {
        if (!user?.organisationId) return;
        organizationApi
            .getOrgProfile(user.organisationId)
            .then(org => {
                setOrgCoverImageKey(org.orgCoverImageKey || null);
                setOrgCurrency(org.currency || "AUD");
            })
            .catch(() => {});
    }, [user?.organisationId]);

    // Load the resolved private-lesson terms (defaults merged in) for the editor.
    useEffect(() => {
        if (!user?.organisationId) return;
        let cancelled = false;
        organizationApi
            .getOrgPolicies(user.organisationId)
            .then(p => {
                if (!cancelled) setPrivatePolicy(p.privateLessons);
            })
            .catch(err => console.error("Failed to load private-lesson terms:", err));
        return () => {
            cancelled = true;
        };
    }, [user?.organisationId]);

    // Load private booking defaults (approval mode + payment timing)
    useEffect(() => {
        const loadBookingSettings = async () => {
            try {
                const settings = await bookingApi.getBookingSettings();
                setApprovalMode(settings.approvalMode || "existing-students");
                setPaymentTiming(settings.paymentTiming || "after-lesson");
            } catch (err) {
                console.error("Error loading booking settings:", err);
            }
        };
        if (user?.organisationId) {
            loadBookingSettings();
        }
    }, [user?.organisationId]);

    // Load instructor profile photo + org cover image
    useEffect(() => {
        const loadImages = async () => {
            if (!user?.organisationId || !profileData) return;

            // Instructor profile photo — versioned cache key
            const photoKey = profileData.instructorImageKey;
            if (photoKey) {
                const photoCacheKey = `${photoKey}_v${profileData.instructorImageVersion}`;
                const cachedPhoto = getCachedImage(photoCacheKey, "instructor-photo");
                if (cachedPhoto) {
                    setInstructorPhotoUrl(base64ToBlobUrl(cachedPhoto));
                } else {
                    const photoResp = await organizationApi.getProfileOverlayImageBase64(photoKey);
                    if (photoResp.ok && photoResp.data?.imageBase64) {
                        cacheImage(photoCacheKey, photoResp.data.imageBase64, "instructor-photo").catch(err =>
                            console.warn("Failed to cache instructor photo:", err)
                        );
                        setInstructorPhotoUrl(base64ToBlobUrl(photoResp.data.imageBase64));
                    }
                }
            }

            // Instructor cover image — versioned cache key (separate from org/school cover)
            try {
                const coverKey = profileData.instructorCoverImageKey;
                const coverVersion = profileData.instructorCoverVersion;
                if (coverKey) {
                    const coverCacheKey = `${coverKey}_v${coverVersion}`;
                    const cachedCover = getCachedImage(coverCacheKey, "instructor-cover");
                    if (cachedCover) {
                        setInstructorCoverImageUrl(base64ToBlobUrl(cachedCover));
                    } else {
                        const coverResp = await webApiClient.get(
                            `/api/v2/instructor/manage/profile/cover-image-base64?imageKey=${encodeURIComponent(coverKey)}`
                        );
                        if (coverResp.ok && coverResp.data?.imageBase64) {
                            cacheImage(coverCacheKey, coverResp.data.imageBase64, "instructor-cover").catch(err =>
                                console.warn("Failed to cache instructor cover image:", err)
                            );
                            setInstructorCoverImageUrl(base64ToBlobUrl(coverResp.data.imageBase64));
                        } else {
                            setInstructorCoverImageUrl(null);
                        }
                    }
                } else {
                    setInstructorCoverImageUrl(null);
                }
            } catch (err) {
                // Cover image is optional — silently ignore
                setInstructorCoverImageUrl(null);
            }
        };
        loadImages();
    }, [user?.organisationId, profileData?.instructorImageVersion, profileData?.instructorCoverVersion]);

    // DanceStyleChips owns the toggling and hands back the full next selection; this
    // routes it through handleInputChange so it marks the form dirty like any other field.
    const handleSetSpecialties = updated => {
        handleInputChange({ target: { name: "specialties", value: updated } });
    };

    // Share Profile Handlers
    // Fetch share data from API (includes webUrl, deepLink, shareText, qrCodeData)
    const fetchShareData = async () => {
        if (!user?.token) return;

        try {
            setShareData(await instructorApi.getShareUrl());
        } catch (err) {
            console.error("Failed to fetch share data:", err);
            // Fallback to manual URL generation
            setShareData({
                webUrl: `${window.location.origin}/instructor/${user.organisationId}`,
                deepLink: `dancelocale://instructor/${user.organisationId}/profile`,
                shareText: `Book a dance lesson with me - View my profile and availability!`,
            });
        }
    };

    const getProfileShareUrl = () => {
        return shareData?.webUrl || `${window.location.origin}/instructor/${user.organisationId}`;
    };

    const getProfileDeepLink = () => {
        return shareData?.deepLink || `dancelocale://instructor/${user.organisationId}/profile`;
    };

    const handleCopyLink = async (linkType = "web") => {
        try {
            const link = linkType === "deep" ? getProfileDeepLink() : getProfileShareUrl();
            await navigator.clipboard.writeText(link);
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        } catch (err) {
            console.error("Failed to copy link:", err);
        }
    };

    const handleGenerateQRCode = async () => {
        if (!showQRCode) {
            try {
                const url = getProfileShareUrl();
                const dataUrl = await QRCode.toDataURL(url, {
                    width: 300,
                    margin: 2,
                    color: {
                        dark: "#451f78",
                        light: "#ffffff",
                    },
                });
                setQrCodeUrl(dataUrl);
                setShowQRCode(true);
            } catch (err) {
                console.error("Error generating QR code:", err);
            }
        } else {
            setShowQRCode(false);
        }
    };

    const handleDownloadQRCode = () => {
        if (!qrCodeUrl) return;

        const link = document.createElement("a");
        link.href = qrCodeUrl;
        link.download = `instructor-profile-qr-${user.organisationId}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Location Management Handlers
    const handleLocationSave = async locationData => {
        try {
            if (editingLocation) {
                // Update existing location
                await instructorApi.updateLocation(user.organisationId, editingLocation.id, {
                    description: locationData.description,
                    address: locationData.address,
                    latitude: locationData.latitude,
                    longitude: locationData.longitude,
                    venueHire: locationData.venueHire, // Include venue hire data
                    isTravel: locationData.isTravel === true,
                });

                await loadProfile();
                setShowLocationPicker(false);
                setEditingLocation(null);
                setSuccessMessage("Location updated successfully!");
                setTimeout(() => setSuccessMessage(""), 3000);
            } else {
                // Add new location
                await instructorApi.addLocation(user.organisationId, {
                    description: locationData.description,
                    address: locationData.address,
                    latitude: locationData.latitude,
                    longitude: locationData.longitude,
                    venueHire: locationData.venueHire, // Include venue hire data
                    isTravel: locationData.isTravel === true,
                });

                await loadProfile();
                setShowLocationPicker(false);
                setSuccessMessage("Location added successfully!");
                setTimeout(() => setSuccessMessage(""), 3000);
            }
        } catch (error) {
            console.error("Error saving location:", error);
            setError("Failed to save location. Please try again.");
        }
    };

    const handleEditLocation = location => {
        setEditingLocation(location);
        setShowLocationPicker(true);
    };

    const handleSetDefaultLocation = async locationId => {
        try {
            await instructorApi.setDefaultLocation(user.organisationId, locationId);

            await loadProfile();
            setSuccessMessage("Default location updated!");
            setTimeout(() => setSuccessMessage(""), 3000);
        } catch (error) {
            console.error("Error setting default location:", error);
            setError("Failed to set default location.");
        }
    };

    const handleToggleLocationStatus = async (locationId, newStatus) => {
        try {
            await instructorApi.toggleLocationStatus(user.organisationId, locationId, newStatus);

            await loadProfile();
            setSuccessMessage(`Location ${newStatus ? "activated" : "deactivated"}!`);
            setTimeout(() => setSuccessMessage(""), 3000);
        } catch (error) {
            console.error("Error toggling location status:", error);
            setError("Failed to update location status.");
        }
    };

    const handleDeleteLocation = locationId => {
        setLocationToDelete(locationId);
        setShowDeleteLocationModal(true);
    };

    const confirmDeleteLocation = async () => {
        const locationId = locationToDelete;
        setShowDeleteLocationModal(false);
        setLocationToDelete(null);

        try {
            await instructorApi.deleteLocation(user.organisationId, locationId);

            await loadProfile();
            setSuccessMessage("Location deleted successfully!");
            setTimeout(() => setSuccessMessage(""), 3000);
        } catch (error) {
            console.error("Error deleting location:", error);
            setError("Failed to delete location.");
        }
    };

    const handleToggleAcceptingBookings = async () => {
        const newValue = !isProfileVisible;
        setIsProfileVisible(newValue);

        try {
            handleInputChange({ target: { name: "isActive", value: newValue } });
            await instructorApi.toggleVisibility(user.organisationId, { isAcceptingBookings: newValue });
            setSuccessMessage(
                newValue ? "You are now accepting bookings" : "Online bookings paused. Students can still message you, and you can still add manual bookings."
            );
            setTimeout(() => setSuccessMessage(""), 3000);
        } catch (error) {
            console.error("Error updating booking availability:", error);
            setError("Failed to update booking availability");
            setIsProfileVisible(!newValue);
        }
    };

    const handleToggleSearchable = async () => {
        const newValue = !isProfileSearchable;
        setIsProfileSearchable(newValue);

        try {
            handleInputChange({ target: { name: "isSearchable", value: newValue } });
            await instructorApi.toggleVisibility(user.organisationId, { isSearchable: newValue });
            setSuccessMessage(newValue ? "Your profile now appears in search" : "Your profile is hidden from search");
            setTimeout(() => setSuccessMessage(""), 3000);
        } catch (error) {
            console.error("Error updating profile searchability:", error);
            setError("Failed to update profile searchability");
            setIsProfileSearchable(!newValue);
        }
    };

    const handleUseOrgCover = async () => {
        setIsApplyingOrgCover(true);
        const imgResp = await organizationApi.getProfileImageBase64(orgCoverImageKey);
        if (imgResp.ok && imgResp.data?.imageBase64) {
            const raw = imgResp.data.imageBase64;
            const pureBase64 = raw.includes(",") ? raw.split(",")[1] : raw;
            const coverResp = await webApiClient.patch("/api/v2/instructor/manage/profile/cover", {
                base64Image: pureBase64,
            });
            if (coverResp.ok) {
                toast.success("Cover image copied to your instructor profile.");
                setInstructorCoverImageUrl(raw);
                loadProfile();
            } else {
                toast.error("Failed to copy the cover image. Please try again.");
            }
        } else {
            toast.error("Could not fetch your Account Details cover image.");
        }
        setIsApplyingOrgCover(false);
    };

    // Fetch a bundled asset URL, compress via canvas to match ImageUpload's quality:0.2 pipeline,
    // and return a data URL. Prevents oversized PNGs from going to S3 uncompressed.
    const urlToBase64 = (url, maxWidth = 1200, quality = 0.2) =>
        fetch(url)
            .then(r => r.blob())
            .then(
                blob =>
                    new Promise((resolve, reject) => {
                        const reader = new FileReader();
                        reader.onloadend = () => {
                            const dataUrl = reader.result;
                            const img = new Image();
                            img.onload = () => {
                                const scale = Math.min(1, maxWidth / img.width);
                                const canvas = document.createElement("canvas");
                                canvas.width = Math.round(img.width * scale);
                                canvas.height = Math.round(img.height * scale);
                                const ctx = canvas.getContext("2d");
                                ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                                resolve(canvas.toDataURL("image/jpeg", quality));
                            };
                            img.onerror = reject;
                            img.src = dataUrl;
                        };
                        reader.onerror = reject;
                        reader.readAsDataURL(blob);
                    })
            );

    const handlePresetSelect = async preset => {
        setApplyingPresetId(preset.id);
        try {
            const dataUrl = await urlToBase64(preset.src);
            setSelectedPresetId(preset.id);
            setPendingPresetBase64(dataUrl);
            setCoverPreviewUrl(dataUrl);
        } catch (err) {
            toast.error("Failed to load preset image. Please try again.");
        } finally {
            setApplyingPresetId(null);
        }
    };

    const handlePresetCancel = () => {
        setSelectedPresetId(null);
        setPendingPresetBase64(null);
        setCoverPreviewUrl(null);
    };

    // Avatar presets are 400px flat illustrations: keep native size and a high JPEG
    // quality so the circle shows no compression blocking (the 0.2 photo setting does).
    const handleAvatarPresetSelect = async preset => {
        setApplyingAvatarPresetId(preset.id);
        try {
            const dataUrl = await urlToBase64(preset.src, 400, 0.85);
            setSelectedAvatarPresetId(preset.id);
            setPendingAvatarPresetBase64(dataUrl);
            setPhotoPreviewUrl(dataUrl);
        } catch (err) {
            toast.error("Failed to load avatar. Please try again.");
        } finally {
            setApplyingAvatarPresetId(null);
        }
    };

    const handleAvatarPresetCancel = () => {
        setSelectedAvatarPresetId(null);
        setPendingAvatarPresetBase64(null);
        setPhotoPreviewUrl(null);
    };

    // `section` identifies which accordion's Save button fired so validation is scoped
    // to that section's own fields (see validateProfile in useInstructorProfile). The
    // payload still carries the full profile — the backend updates per-field — but the
    // user is no longer blocked by a field in an accordion they didn't touch.
    const handleSubmit = async (e, section) => {
        e.preventDefault();

        if (!validateProfile(section)) {
            window.scrollTo({ top: 0, behavior: "smooth" });
            return;
        }

        // If a preset avatar is pending, save it alongside the profile
        if (pendingAvatarPresetBase64) {
            const pureAvatarBase64 = pendingAvatarPresetBase64.includes(",")
                ? pendingAvatarPresetBase64.split(",")[1]
                : pendingAvatarPresetBase64;
            const photoResp = await webApiClient.patch("/api/v2/instructor/manage/profile/photo", {
                base64Image: pureAvatarBase64,
            });
            if (photoResp.ok) {
                const newKey = photoResp.data?.profile?.instructorProfile?.imageKey;
                const newVersion = photoResp.data?.profile?.instructorProfile?.imageVersion;
                if (newKey && newVersion) {
                    cacheImage(`${newKey}_v${newVersion}`, pendingAvatarPresetBase64, "instructor-photo").catch(
                        () => {}
                    );
                }
                setInstructorPhotoUrl(pendingAvatarPresetBase64);
                setSelectedAvatarPresetId(null);
                setPendingAvatarPresetBase64(null);
                setPhotoPreviewUrl(null);
            } else {
                toast.error("Failed to save profile photo. Please try again.");
                return;
            }
        }

        // If a preset cover is pending, save it alongside the profile
        if (pendingPresetBase64) {
            const pureBase64 = pendingPresetBase64.includes(",")
                ? pendingPresetBase64.split(",")[1]
                : pendingPresetBase64;
            const coverResp = await webApiClient.patch("/api/v2/instructor/manage/profile/cover", {
                base64Image: pureBase64,
            });
            if (coverResp.ok) {
                const newKey = coverResp.data?.profile?.instructorProfile?.coverImageKey;
                const newVersion = coverResp.data?.profile?.instructorProfile?.coverVersion;
                if (newKey && newVersion) {
                    cacheImage(`${newKey}_v${newVersion}`, pendingPresetBase64, "instructor-cover").catch(() => {});
                }
                setInstructorCoverImageUrl(pendingPresetBase64);
                setSelectedPresetId(null);
                setPendingPresetBase64(null);
                setCoverPreviewUrl(null);
            } else {
                toast.error("Failed to save cover image. Please try again.");
                return;
            }
        }

        const success = await saveProfile();
        if (success) {
            window.scrollTo({ top: 0, behavior: "smooth" });
        }
    };

    const handleSaveBookingSettings = async () => {
        try {
            setIsSavingBookingSettings(true);
            await bookingApi.updateBookingSettings({ approvalMode, paymentTiming });
            toast.success("Booking defaults saved!");
        } catch (err) {
            console.error("Error saving booking settings:", err);
            toast.error(err.message || "Failed to save booking defaults. Please try again.");
        } finally {
            setIsSavingBookingSettings(false);
        }
    };

    // Save the private-lesson terms and conditions (partial save — preserves the
    // class-enrolment surface edited in Settings).
    const handleSaveTerms = async () => {
        if (!privatePolicy) return;
        setIsSavingTerms(true);
        try {
            const updated = await organizationApi.updateOrgPolicies(user.organisationId, {
                privateLessons: privatePolicy,
            });
            setPrivatePolicy(updated.privateLessons);
            toast.success("Terms and conditions saved");
        } catch (err) {
            toast.error(err.message || "Failed to save terms and conditions");
        } finally {
            setIsSavingTerms(false);
        }
    };

    if (isLoading) {
        return (
            <div className="dashboard-container org-console">
                <div className="dashboard-main">
                    <div className="dashboard-content-wrapper">
                        <div className="portal-header-card">
                            <p style={{ textAlign: "center", color: "var(--dl-color-gray-600)" }}>Loading profile...</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="dashboard-container org-console">
            {/* Navigation Header - Copied from InstructorPortal.js */}
            <AppHeader />

            {/* Main Content */}
            <div className="dashboard-main">
                <div className="dashboard-content-wrapper">
                    {/* Form — single frosted panel, mirrors the org Settings page (no sub-portal header card) */}
                    <form onSubmit={handleSubmit} className="instructor-profile-form">
                        <div className="tab-content-settings">
                            {/* Page header — org Settings style (back button + title inside the panel) */}
                            <div className="portal-header-title-row">
                                <button type="button" onClick={() => navigate("/instructor-dashboard")} className="back-nav-btn">
                                    ← Instructor Dashboard
                                </button>
                                <h1 className="portal-header-title">Instructor Details</h1>
                            </div>

                            {/* Section heading for the whole Public Profile area (the visibility
                            card + the accordions that follow). */}
                            <div
                                className="form-section"
                                style={{ marginBottom: "16px", borderBottom: "none", paddingBottom: 0 }}>
                                <h2 className="section-title">Public Profile</h2>
                                <p className="section-description">
                                    Your teaching name and photo appear on all booking cards, emails, and student-facing
                                    screens.
                                </p>
                            </div>

                            {/* Profile Visibility — a non-collapsible accordion card (same frosted
                            card styling as the accordions below, but no chevron / always open).
                            Two independent toggles: searchable in public search, and accepting
                            public bookings. An instructor can be findable without taking public
                            bookings (they still get a "not accepting bookings" note on their
                            public profile and can keep taking manual/message bookings). */}
                            <div className="settings-accordion-section">
                                <div className="settings-accordion-header static expanded">
                                    <div className="accordion-title-group">
                                        <h4>Profile Visibility</h4>
                                        <span className="accordion-title-hint">Search &amp; booking</span>
                                    </div>
                                </div>
                                <div className="settings-accordion-content">
                                    <div className="instructor-visibility-row">
                                        <p className="instructor-visibility-hint">
                                            Show your profile in public search so new students can find you and send
                                            messages, or book you based on the <strong>Accept bookings</strong> toggle.
                                        </p>
                                        <label className="dashboard-toggle-ios" style={{ cursor: "pointer" }}>
                                            <input
                                                type="checkbox"
                                                checked={isProfileSearchable}
                                                onChange={handleToggleSearchable}
                                            />
                                            <span className="toggle-slider"></span>
                                            <span className="toggle-label" style={{ marginLeft: "8px" }}>
                                                Show in public search?
                                            </span>
                                        </label>
                                    </div>

                                    <div className="instructor-visibility-row">
                                        <p className="instructor-visibility-hint">
                                            Let students book you directly from your public calendar. Turn this off and
                                            you can still make bookings manually after students message you.
                                        </p>
                                        <label className="dashboard-toggle-ios" style={{ cursor: "pointer" }}>
                                            <input
                                                type="checkbox"
                                                checked={isProfileVisible}
                                                onChange={handleToggleAcceptingBookings}
                                            />
                                            <span className="toggle-slider"></span>
                                            <span className="toggle-label" style={{ marginLeft: "8px" }}>
                                                Accept bookings - App/Web?
                                            </span>
                                        </label>
                                    </div>
                                </div>
                            </div>

                            {/* ── Accordion 1: Profile Card ── */}
                            <div className="settings-accordion-section">
                                <div
                                    className={`settings-accordion-header ${accordion.profileCard ? "expanded" : ""}`}
                                    onClick={() => toggleAccordion("profileCard")}>
                                    <div className="accordion-title-group">
                                        <h4>Profile Card</h4>
                                        <span className="accordion-title-hint">Public facing images and info</span>
                                    </div>
                                    <span className="accordion-icon">{accordion.profileCard ? "−" : "+"}</span>
                                </div>
                                {accordion.profileCard && (
                                    <div className="settings-accordion-content">
                                        <div className="profile-identity-layout">
                                            {/* Left: photo + cover upload only */}
                                            <div className="profile-identity-fields">
                                                <div className="form-group">
                                                    <label className="form-label">Profile Photo</label>
                                                    <p className="form-hint">
                                                        Your photo appears on booking cards, emails, and student-facing
                                                        screens.
                                                    </p>
                                                    <InstructorAvatarUpload
                                                        currentImage={instructorPhotoUrl}
                                                        organisationId={user.organisationId}
                                                        onPreviewChange={setPhotoPreviewUrl}
                                                        externalPendingBase64={pendingAvatarPresetBase64}
                                                        onExternalCancel={handleAvatarPresetCancel}
                                                        onSaveSuccess={(base64, responseData) => {
                                                            setPhotoPreviewUrl(null);
                                                            setSelectedAvatarPresetId(null);
                                                            setPendingAvatarPresetBase64(null);
                                                            setInstructorPhotoUrl(base64);
                                                            const newKey =
                                                                responseData?.profile?.instructorProfile?.imageKey;
                                                            const newVersion =
                                                                responseData?.profile?.instructorProfile?.imageVersion;
                                                            if (newKey && newVersion) {
                                                                cacheImage(
                                                                    `${newKey}_v${newVersion}`,
                                                                    base64,
                                                                    "instructor-photo"
                                                                ).catch(() => {});
                                                            }
                                                            loadProfile();
                                                        }}
                                                        onSaveError={() => {}}
                                                    />
                                                    {/* Preset avatar picker: sits under the photo circle, above Cover Image */}
                                                    <InstructorAvatarPresets
                                                        presets={INSTRUCTOR_AVATAR_PRESETS}
                                                        selectedId={selectedAvatarPresetId}
                                                        applyingId={applyingAvatarPresetId}
                                                        onSelect={handleAvatarPresetSelect}
                                                    />
                                                </div>

                                                <div className="form-group" style={{ marginTop: "20px" }}>
                                                    <label className="form-label">Cover Image</label>
                                                    <p className="form-hint">
                                                        The background image on your booking card. Recommended landscape
                                                        orientation.
                                                    </p>
                                                    {/* The copy-from-account offer lives INSIDE the same condition as the
                                                        button. Kept outside, it advertised a cover image to accounts that
                                                        have none set, pointing at a section that does not exist. */}
                                                    {!instructorCoverImageUrl && orgCoverImageKey && (
                                                        <div style={{ marginBottom: "8px" }}>
                                                            <p className="form-hint">
                                                                Or copy the cover image from your Account Details
                                                                settings.
                                                            </p>
                                                            <button
                                                                type="button"
                                                                className="btn-turquoise btn-small"
                                                                onClick={handleUseOrgCover}
                                                                disabled={isApplyingOrgCover}>
                                                                {isApplyingOrgCover
                                                                    ? "Applying..."
                                                                    : "Copy my Account Details cover"}
                                                            </button>
                                                        </div>
                                                    )}
                                                    <InstructorCoverUpload
                                                        currentImage={instructorCoverImageUrl}
                                                        onPreviewChange={setCoverPreviewUrl}
                                                        externalPendingBase64={pendingPresetBase64}
                                                        onExternalCancel={handlePresetCancel}
                                                        onDeleteSuccess={() => {
                                                            setInstructorCoverImageUrl(null);
                                                            setSelectedPresetId(null);
                                                            setPendingPresetBase64(null);
                                                            loadProfile();
                                                        }}
                                                        onSaveSuccess={(base64, responseData) => {
                                                            const newKey =
                                                                responseData?.profile?.instructorProfile?.coverImageKey;
                                                            const newVersion =
                                                                responseData?.profile?.instructorProfile?.coverVersion;
                                                            if (newKey && newVersion) {
                                                                cacheImage(
                                                                    `${newKey}_v${newVersion}`,
                                                                    base64,
                                                                    "instructor-cover"
                                                                ).catch(() => {});
                                                            }
                                                            setInstructorCoverImageUrl(base64);
                                                            setSelectedPresetId(null);
                                                            setPendingPresetBase64(null);
                                                            loadProfile();
                                                        }}
                                                        onSaveError={() => {}}
                                                    />
                                                </div>
                                            </div>

                                            {/* Right: live card preview only */}
                                            <div className="profile-card-preview-wrapper">
                                                <p className="profile-card-preview-label">
                                                    Student-facing card preview
                                                </p>
                                                <InstructorCard
                                                    instructor={{
                                                        name:
                                                            profileData.teachingName ||
                                                            user?.orgName ||
                                                            "Your Teaching Name",
                                                        profileImageUrl: photoPreviewUrl || instructorPhotoUrl || null,
                                                        coverImageUrl:
                                                            coverPreviewUrl ||
                                                            instructorCoverImageUrl ||
                                                            coverPlaceholderUrl,
                                                        location: profileData.preferredLocations?.[0] || null,
                                                        danceStyles: profileData.specialties || [],
                                                        bio: profileData.bio || "",
                                                        teachingFormats: [],
                                                        isVerified: false,
                                                    }}
                                                />
                                            </div>
                                        </div>

                                        {/* Preset cover picker — full-width row below photo/cover | card preview */}
                                        <InstructorCoverPresets
                                            presets={INSTRUCTOR_COVER_PRESETS}
                                            selectedId={selectedPresetId}
                                            applyingId={applyingPresetId}
                                            onSelect={handlePresetSelect}
                                        />

                                        {/* Teaching name + save — full-width below preset picker */}
                                        <div className="form-group" style={{ marginTop: "20px" }}>
                                            <label htmlFor="teachingName" className="form-label">
                                                Teaching Name
                                            </label>
                                            <p className="form-hint">
                                                The name students see on bookings and emails. Leave blank to use your
                                                school name.
                                            </p>
                                            <input
                                                type="text"
                                                id="teachingName"
                                                name="teachingName"
                                                value={profileData.teachingName}
                                                onChange={handleInputChange}
                                                className="form-input"
                                                placeholder="e.g. Maria Gomez"
                                                maxLength={100}
                                            />
                                        </div>

                                        {/* Dance styles — shown on the student-facing card, so edited here */}
                                        <div className="form-group" style={{ marginTop: "20px" }}>
                                            <label className="form-label">Dance Style Specialties</label>
                                            <p className="form-hint">
                                                The styles shown on your booking card and public profile.
                                            </p>
                                            <DanceStyleChips
                                                selected={profileData.specialties || []}
                                                onChange={handleSetSpecialties}
                                            />
                                        </div>

                                        {/* Hourly Rate — public-facing pricing, so it lives in the Profile Card accordion */}
                                        <div className="form-group" style={{ marginTop: "20px" }}>
                                            <label htmlFor="hourlyRate" className="form-label">
                                                Hourly Rate ({orgCurrency}) <span className="required">*</span>
                                            </label>
                                            <p className="form-hint">
                                                This is your hourly rate for Home Schedule availability, and is in your
                                                base currency, which is set in{" "}
                                                <button
                                                    type="button"
                                                    className="hint-link"
                                                    onClick={() => navigate("/settings")}>
                                                    Settings
                                                </button>
                                                . When you travel, each travel period sets its own rate and currency.
                                            </p>
                                            <input
                                                type="number"
                                                id="hourlyRate"
                                                name="hourlyRate"
                                                value={profileData.hourlyRate}
                                                onChange={handleInputChange}
                                                onFocus={e => e.target.select()}
                                                step="1"
                                                min="0"
                                                className="form-input hourly-rate-input"
                                                placeholder="e.g., 75"
                                                required
                                            />
                                        </div>

                                        <div style={{ marginTop: "12px" }}>
                                            <button
                                                type="button"
                                                className="btn-save"
                                                disabled={isSaving}
                                                onClick={e => handleSubmit(e, "profileCard")}>
                                                {isSaving ? "Saving..." : "Save Profile Details"}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* ── Accordion 2: About You ── */}
                            <div className="settings-accordion-section">
                                <div
                                    className={`settings-accordion-header ${accordion.about ? "expanded" : ""}`}
                                    onClick={() => toggleAccordion("about")}>
                                    <div className="accordion-title-group">
                                        <h4>About You</h4>
                                        <span className="accordion-title-hint">bio, daily, quals</span>
                                    </div>
                                    <span className="accordion-icon">{accordion.about ? "−" : "+"}</span>
                                </div>
                                {accordion.about && (
                                    <div className="settings-accordion-content">
                                        <div className="form-group">
                                            <label htmlFor="bio" className="form-label">
                                                Bio / Introduction <span className="required">*</span>
                                            </label>
                                            <p className="form-hint">
                                                {profileData.bio.length} / 50 minimum characters
                                            </p>
                                            <textarea
                                                id="bio"
                                                name="bio"
                                                value={profileData.bio}
                                                onChange={handleInputChange}
                                                rows="5"
                                                className="form-textarea"
                                                placeholder="Tell students about your teaching style, dance background, and what makes you unique as an instructor..."
                                                required
                                            />
                                        </div>

                                        <div className="form-group">
                                            <label htmlFor="dailyMessage" className="form-label">
                                                Daily Feels
                                            </label>
                                            <textarea
                                                id="dailyMessage"
                                                name="dailyMessage"
                                                value={profileData.dailyMessage}
                                                onChange={handleInputChange}
                                                className="form-input"
                                                rows={3}
                                                placeholder="Show a live update on your profile, e.g. 'I have afternoon lessons available today!' Leave blank to hide."
                                            />
                                        </div>

                                        <div className="form-group">
                                            <label htmlFor="qualifications" className="form-label">
                                                Qualifications / Certifications
                                            </label>
                                            <textarea
                                                id="qualifications"
                                                name="qualifications"
                                                value={profileData.qualifications}
                                                onChange={handleInputChange}
                                                rows="3"
                                                className="form-textarea"
                                                placeholder="List your dance certifications, awards, or notable achievements..."
                                            />
                                        </div>

                                        <div style={{ marginTop: "12px" }}>
                                            <button
                                                type="button"
                                                className="btn-save"
                                                disabled={isSaving}
                                                onClick={e => handleSubmit(e, "about")}>
                                                {isSaving ? "Saving..." : "Save Profile Details"}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* ── Accordion 3: Share & Socials ── */}
                            <div className="settings-accordion-section">
                                <div
                                    className={`settings-accordion-header ${accordion.share ? "expanded" : ""}`}
                                    onClick={() => toggleAccordion("share")}>
                                    <div className="accordion-title-group">
                                        <h4>Share &amp; Socials</h4>
                                        <span className="accordion-title-hint">QR links + social links</span>
                                    </div>
                                    <span className="accordion-icon">{accordion.share ? "−" : "+"}</span>
                                </div>
                                {accordion.share && (
                                    <div className="settings-accordion-content">
                                        {/* Share Your Profile Section */}
                                        {hasExistingProfile && (
                                            <div className="form-section">
                                                <h2 className="section-title">
                                                    <Icon
                                                        path={mdiShareVariant}
                                                        size={1}
                                                        style={{ marginRight: "8px", verticalAlign: "middle" }}
                                                    />
                                                    Share Your Profile
                                                </h2>
                                                <p className="section-description">
                                                    Share your profile link so students can view your availability and
                                                    book lessons with you. Add this link to your Instagram bio, website,
                                                    or business cards.
                                                </p>

                                                {/* Profile Link */}
                                                <div className="share-profile-url-container">
                                                    <label className="share-url-label">Your Profile Link</label>
                                                    <p className="share-url-hint">
                                                        Share anywhere: Instagram bio, email, business cards, SMS
                                                    </p>
                                                    <div>
                                                        <input
                                                            type="text"
                                                            value={getProfileShareUrl()}
                                                            readOnly
                                                            className="share-profile-url-input"
                                                        />
                                                        <button
                                                            type="button"
                                                            onClick={() => handleCopyLink("web")}
                                                            className="btn-copy-link"
                                                            title="Copy profile link to clipboard">
                                                            <Icon path={mdiContentCopy} size={0.8} />
                                                            {copySuccess ? "Copied!" : "Copy Link"}
                                                        </button>
                                                    </div>
                                                </div>

                                                {/* QR Code Actions */}
                                                <div className="share-profile-actions">
                                                    <button
                                                        type="button"
                                                        onClick={handleGenerateQRCode}
                                                        className="btn-qr-code">
                                                        <Icon path={mdiQrcode} size={0.8} />
                                                        {showQRCode ? "Hide QR Code" : "Show QR Code"}
                                                    </button>
                                                    <a
                                                        href={getProfileShareUrl()}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="btn-preview-profile">
                                                        <Icon path={mdiOpenInNew} size={0.8} />
                                                        Preview Profile
                                                    </a>

                                                    {showQRCode && qrCodeUrl && (
                                                        <button
                                                            type="button"
                                                            onClick={handleDownloadQRCode}
                                                            className="btn-download-qr">
                                                            <Icon path={mdiDownload} size={0.8} />
                                                            Download QR Code
                                                        </button>
                                                    )}
                                                </div>

                                                {/* QR Code Display */}
                                                {showQRCode && qrCodeUrl && (
                                                    <div className="qr-code-display">
                                                        <p className="qr-code-hint">
                                                            Click Download QR Code to save this image for your
                                                            marketing materials
                                                        </p>
                                                        <img
                                                            src={qrCodeUrl}
                                                            alt="Profile QR Code"
                                                            className="qr-code-image"
                                                        />
                                                        <p className="qr-code-hint">
                                                            Students can scan this QR code to view your instructor
                                                            profile and download the mobile app to book lessons
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* Social Links Section */}
                                        <div className="form-section">
                                            <h2 className="section-title">
                                                <Icon
                                                    path={mdiInstagram}
                                                    size={1}
                                                    style={{ marginRight: "8px", verticalAlign: "middle" }}
                                                />
                                                Social Links
                                            </h2>
                                            <p className="section-description">
                                                Add your Instagram, Facebook, and website so students can find you
                                                online. These appear on your public profile.
                                            </p>

                                            <div className="form-group">
                                                <label htmlFor="instagram" className="form-label">
                                                    <Icon
                                                        path={mdiInstagram}
                                                        size={0.7}
                                                        style={{ marginRight: "6px", verticalAlign: "middle" }}
                                                    />
                                                    Instagram
                                                </label>
                                                <p className="form-hint">Your full Instagram profile link</p>
                                                <input
                                                    type="text"
                                                    inputMode="url"
                                                    id="instagram"
                                                    name="instagram"
                                                    value={profileData.instagram}
                                                    onChange={handleInputChange}
                                                    onBlur={e => handleLinkBlur("instagram", e.target.value)}
                                                    className="form-input"
                                                    placeholder="https://instagram.com/yourhandle"
                                                />
                                                {correctedLinks.instagram && (
                                                    <p className="link-corrected-hint">Added https:// for you</p>
                                                )}
                                            </div>

                                            <div className="form-group" style={{ marginTop: "16px" }}>
                                                <label htmlFor="facebook" className="form-label">
                                                    <Icon
                                                        path={mdiFacebook}
                                                        size={0.7}
                                                        style={{ marginRight: "6px", verticalAlign: "middle" }}
                                                    />
                                                    Facebook
                                                </label>
                                                <p className="form-hint">Your full Facebook page link</p>
                                                <input
                                                    type="text"
                                                    inputMode="url"
                                                    id="facebook"
                                                    name="facebook"
                                                    value={profileData.facebook}
                                                    onChange={handleInputChange}
                                                    onBlur={e => handleLinkBlur("facebook", e.target.value)}
                                                    className="form-input"
                                                    placeholder="https://facebook.com/yourpage"
                                                />
                                                {correctedLinks.facebook && (
                                                    <p className="link-corrected-hint">Added https:// for you</p>
                                                )}
                                            </div>

                                            <div className="form-group" style={{ marginTop: "16px" }}>
                                                <label htmlFor="website" className="form-label">
                                                    <Icon
                                                        path={mdiWeb}
                                                        size={0.7}
                                                        style={{ marginRight: "6px", verticalAlign: "middle" }}
                                                    />
                                                    Website
                                                </label>
                                                <p className="form-hint">Your full website link</p>
                                                <input
                                                    type="text"
                                                    inputMode="url"
                                                    id="website"
                                                    name="website"
                                                    value={profileData.website}
                                                    onChange={handleInputChange}
                                                    onBlur={e => handleLinkBlur("website", e.target.value)}
                                                    className="form-input"
                                                    placeholder="https://yourwebsite.com"
                                                />
                                                {correctedLinks.website && (
                                                    <p className="link-corrected-hint">Added https:// for you</p>
                                                )}
                                            </div>

                                            <div style={{ marginTop: "16px" }}>
                                                <button
                                                    type="button"
                                                    className="btn-save"
                                                    disabled={isSaving}
                                                    onClick={e => handleSubmit(e, "share")}>
                                                    {isSaving ? "Saving..." : "Save Profile Details"}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* ── Accordion 4: Private Booking Defaults ── */}
                            <div className="settings-accordion-section">
                                <div
                                    className={`settings-accordion-header ${accordion.bookingDefaults ? "expanded" : ""}`}
                                    onClick={() => toggleAccordion("bookingDefaults")}>
                                    <div className="accordion-title-group">
                                        <h4>Private Booking Defaults</h4>
                                        <span className="accordion-title-hint">approval/timing</span>
                                    </div>
                                    <span className="accordion-icon">{accordion.bookingDefaults ? "−" : "+"}</span>
                                </div>
                                {accordion.bookingDefaults && (
                                    <div className="settings-accordion-content">
                                        {/* Private Booking Defaults Section */}
                                        <div className="form-section booking-defaults-section">
                                            <h2 className="section-title">Private Booking Defaults</h2>
                                            <p className="section-description">
                                                Control how booking requests are approved and when students pay for
                                                their private lessons.
                                            </p>

                                            <div className="form-row">
                                                {/* Payment Timing — LEFT column. It gates whether the approval
                                    setting applies, so it comes first. */}
                                                <div className="form-group">
                                                    <label className="form-label">Payment Timing</label>
                                                    <p className="form-hint">
                                                        Choose when students are required to pay for their lesson.
                                                    </p>
                                                    {/* Guest-prepay disclosure — prominent purple banner above the options */}
                                                    <div className="booking-defaults-notice">
                                                        Guests who book without an account always pay upfront and are
                                                        confirmed instantly, whichever timing you choose.
                                                    </div>
                                                    {[
                                                        {
                                                            value: "after-lesson",
                                                            label: "After the private lesson",
                                                            hint: "Students pay once the lesson is complete. Best suited to students you already know and trust. See Booking Request Approval for how these bookings are confirmed.",
                                                        },
                                                        {
                                                            value: "before-lesson",
                                                            label: "Before the private lesson",
                                                            hint: "Students pay when they book. The booking is confirmed automatically once payment is received.",
                                                        },
                                                    ].map(opt => (
                                                        <div key={opt.value} style={{ marginBottom: "12px" }}>
                                                            <CustomFormInput
                                                                type="radio"
                                                                variant="purple"
                                                                layout="standard"
                                                                id={`paymentTiming-${opt.value}`}
                                                                name="paymentTiming"
                                                                value={opt.value}
                                                                label={opt.label}
                                                                checked={paymentTiming === opt.value}
                                                                onChange={() => setPaymentTiming(opt.value)}
                                                            />
                                                            <p className="form-hint booking-defaults-option-hint">
                                                                {opt.hint}
                                                            </p>
                                                        </div>
                                                    ))}
                                                </div>

                                                {/* Booking Request Approval — RIGHT column. Only applies to
                                    pay-later bookings; disabled when payment is taken before the lesson. */}
                                                <div
                                                    className="form-group"
                                                    style={{ display: "flex", flexDirection: "column" }}>
                                                    <label className="form-label">Booking Request Approval</label>
                                                    <p className="form-hint">
                                                        Choose how pay-later booking requests are handled. Prepaid
                                                        bookings are always confirmed once payment completes.
                                                    </p>
                                                    {paymentTiming === "before-lesson" && (
                                                        <div className="booking-defaults-disabled-note">
                                                            These options are turned off because you collect payment
                                                            before the lesson. With before-lesson payment, every booking
                                                            is automatically confirmed the moment it is paid, so there
                                                            is nothing to approve.
                                                        </div>
                                                    )}
                                                    <div
                                                        style={{
                                                            opacity: paymentTiming === "before-lesson" ? 0.55 : 1,
                                                            pointerEvents:
                                                                paymentTiming === "before-lesson" ? "none" : "auto",
                                                        }}>
                                                        {[
                                                            {
                                                                value: "manual",
                                                                label: "Require approval",
                                                                hint: "All pay-later booking requests are held for your review, including those from your existing students.",
                                                            },
                                                            {
                                                                value: "existing-students",
                                                                label: "Auto-approve my existing students",
                                                                hint: "Pay-later requests from your existing students are confirmed instantly. People new to you are held for your approval.",
                                                            },
                                                            {
                                                                value: "all",
                                                                label: "Auto-approve all",
                                                                hint: "All booking requests are confirmed instantly.",
                                                            },
                                                        ].map(opt => (
                                                            <div key={opt.value} style={{ marginBottom: "12px" }}>
                                                                <CustomFormInput
                                                                    type="radio"
                                                                    variant="purple"
                                                                    layout="standard"
                                                                    id={`approvalMode-${opt.value}`}
                                                                    name="approvalMode"
                                                                    value={opt.value}
                                                                    label={opt.label}
                                                                    checked={approvalMode === opt.value}
                                                                    onChange={() => setApprovalMode(opt.value)}
                                                                    disabled={paymentTiming === "before-lesson"}
                                                                />
                                                                <p className="form-hint booking-defaults-option-hint">
                                                                    {opt.hint}
                                                                </p>
                                                            </div>
                                                        ))}
                                                    </div>
                                                    <div
                                                        style={{
                                                            marginTop: "auto",
                                                            display: "flex",
                                                            justifyContent: "flex-end",
                                                            paddingTop: "16px",
                                                        }}>
                                                        <button
                                                            type="button"
                                                            className="btn-purple btn-small"
                                                            style={{ padding: "8px 12px" }}
                                                            onClick={handleSaveBookingSettings}
                                                            disabled={isSavingBookingSettings}>
                                                            <span
                                                                style={{
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    gap: "6px",
                                                                }}>
                                                                <Icon path={mdiContentSaveCogOutline} size={0.8} />
                                                                {isSavingBookingSettings
                                                                    ? "Saving..."
                                                                    : "Save Booking Defaults"}
                                                            </span>
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* ── Accordion 5: Private Lesson Terms and Conditions ── */}
                            <div className="settings-accordion-section">
                                <div
                                    className={`settings-accordion-header ${accordion.terms ? "expanded" : ""}`}
                                    onClick={() => toggleAccordion("terms")}>
                                    <div className="accordion-title-group">
                                        <h4>Your Terms and Conditions</h4>
                                        <span className="accordion-title-hint">shown at checkout</span>
                                    </div>
                                    <span className="accordion-icon">{accordion.terms ? "−" : "+"}</span>
                                </div>
                                {accordion.terms && (
                                    <div className="settings-accordion-content">
                                        <div className="form-section">
                                            <p className="section-description">
                                                These are your private lesson terms and conditions. They are shown to
                                                students at checkout, where they tick a box to agree before paying. Each
                                                field is pre-filled with suggested wording you can keep or edit.
                                            </p>
                                            {privatePolicy ? (
                                                <>
                                                    {SUPPLIER_POLICY_EDITOR_FIELDS.map(({ key, label }) => (
                                                        <div className="form-group" key={key}>
                                                            <label className="form-label">{label}</label>
                                                            <textarea
                                                                className="form-textarea"
                                                                value={privatePolicy[key] || ""}
                                                                onChange={e =>
                                                                    setPrivatePolicy(prev => ({
                                                                        ...prev,
                                                                        [key]: e.target.value,
                                                                    }))
                                                                }
                                                                maxLength={2000}
                                                                rows={2}
                                                            />
                                                        </div>
                                                    ))}
                                                    <div
                                                        style={{
                                                            display: "flex",
                                                            justifyContent: "flex-end",
                                                            paddingTop: "16px",
                                                        }}>
                                                        <button
                                                            type="button"
                                                            className="btn-purple btn-small"
                                                            style={{ padding: "8px 12px" }}
                                                            onClick={handleSaveTerms}
                                                            disabled={isSavingTerms}>
                                                            <span
                                                                style={{
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    gap: "6px",
                                                                }}>
                                                                <Icon path={mdiContentSaveCogOutline} size={0.8} />
                                                                {isSavingTerms
                                                                    ? "Saving..."
                                                                    : "Save Terms and Conditions"}
                                                            </span>
                                                        </button>
                                                    </div>
                                                </>
                                            ) : (
                                                <p className="form-hint">Loading terms and conditions...</p>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* ── Accordion 6: Timezones & Teaching Locations ── */}
                            <div className="settings-accordion-section">
                                <div
                                    className={`settings-accordion-header ${accordion.locations ? "expanded" : ""}`}
                                    onClick={() => toggleAccordion("locations")}>
                                    <div className="accordion-title-group">
                                        <h4>Timezones &amp; Teaching Locations</h4>
                                        <span className="accordion-title-hint">where you teach, and where you are</span>
                                    </div>
                                    <span className="accordion-icon">{accordion.locations ? "−" : "+"}</span>
                                </div>
                                {accordion.locations && (
                                    <div className="settings-accordion-content">
                                        {/* Home Timezone Section */}
                                        <div className="form-section">
                                            <h2 className="section-title">Home Timezone</h2>
                                            <p className="section-description">
                                                Set the timezone where you teach most of the time. This is used to
                                                display your availability windows correctly to students. When you
                                                travel, your travel schedule uses the destination timezone
                                                automatically. This setting is not affected by travel bookings.
                                            </p>
                                            <div className="form-group">
                                                <label htmlFor="timezone" className="form-label">
                                                    Timezone <span className="required">*</span>
                                                </label>
                                                <p className="form-hint">
                                                    Only change this if you permanently relocate, not for trips.
                                                </p>
                                                <select
                                                    id="timezone"
                                                    name="timezone"
                                                    value={profileData.timezone}
                                                    onChange={handleInputChange}
                                                    className="form-select"
                                                    required>
                                                    {timezoneOptions.map(tz => (
                                                        <option key={tz.value} value={tz.value}>
                                                            {tz.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>

                                        {/* Teaching Locations Section */}
                                        {(() => {
                                            const allLocations = profileData.preferredLocations || [];
                                            const sortedLocations = [...allLocations].sort((a, b) => {
                                                if (a.isDefault && !b.isDefault) return -1;
                                                if (!a.isDefault && b.isDefault) return 1;
                                                if (a.isActive !== false && b.isActive === false) return -1;
                                                if (a.isActive === false && b.isActive !== false) return 1;
                                                return 0;
                                            });
                                            const homeLocations = sortedLocations.filter(loc => loc.isTravel !== true);
                                            const travelLocations = sortedLocations.filter(
                                                loc => loc.isTravel === true
                                            );

                                            const renderLocationCard = (location, index) => {
                                                const cardId = location.id || `idx-${index}`;
                                                const isExpanded =
                                                    location.isDefault || expandedLocationIds.includes(cardId);
                                                const isCollapsible = !location.isDefault;

                                                return (
                                                    <div
                                                        key={cardId}
                                                        className={`location-card${isCollapsible ? " is-collapsible" : ""}${isExpanded ? " is-expanded" : " is-collapsed"}`}>
                                                        {isCollapsible ? (
                                                            <button
                                                                type="button"
                                                                className="location-card-header location-card-header-toggle"
                                                                onClick={() => toggleLocationExpanded(cardId)}
                                                                aria-expanded={isExpanded}>
                                                                <div
                                                                    style={{
                                                                        display: "flex",
                                                                        alignItems: "center",
                                                                        gap: "12px",
                                                                    }}>
                                                                    <Icon
                                                                        path={
                                                                            isExpanded
                                                                                ? mdiChevronDown
                                                                                : mdiChevronRight
                                                                        }
                                                                        size={0.8}
                                                                        className="location-card-chevron"
                                                                    />
                                                                    <h4 style={{ margin: 0 }}>
                                                                        {typeof location.description === "string"
                                                                            ? location.description
                                                                            : JSON.stringify(location.description)}
                                                                    </h4>
                                                                </div>
                                                                <div className="location-badges">
                                                                    {!location.isActive && (
                                                                        <span className="location-badge inactive">
                                                                            Inactive
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </button>
                                                        ) : (
                                                            <div className="location-card-header">
                                                                <div
                                                                    style={{
                                                                        display: "flex",
                                                                        alignItems: "center",
                                                                        gap: "12px",
                                                                    }}>
                                                                    <h4 style={{ margin: 0 }}>
                                                                        {typeof location.description === "string"
                                                                            ? location.description
                                                                            : JSON.stringify(location.description)}
                                                                    </h4>
                                                                    <span className="location-badge default">
                                                                        Default
                                                                    </span>
                                                                </div>
                                                                <div className="location-badges">
                                                                    {!location.isActive && (
                                                                        <span className="location-badge inactive">
                                                                            Inactive
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        )}

                                                        {isExpanded && (
                                                            <>
                                                                <div className="location-card-details">
                                                                    {location.address &&
                                                                        typeof location.address === "string" && (
                                                                            <div className="location-detail-row">
                                                                                <Icon
                                                                                    path={mdiMapMarker}
                                                                                    size={0.7}
                                                                                    className="location-icon"
                                                                                />
                                                                                <span>{location.address}</span>
                                                                            </div>
                                                                        )}
                                                                    {location.locationDescription &&
                                                                        typeof location.locationDescription ===
                                                                            "string" && (
                                                                            <div className="location-detail-row location-description">
                                                                                <Icon
                                                                                    path={mdiInformationOutline}
                                                                                    size={0.7}
                                                                                    className="location-icon"
                                                                                />
                                                                                <span
                                                                                    style={{
                                                                                        color: "var(--dl-color-gray-600)",
                                                                                        fontSize: "0.9rem",
                                                                                        fontStyle: "italic",
                                                                                    }}>
                                                                                    {location.locationDescription}
                                                                                </span>
                                                                            </div>
                                                                        )}
                                                                    {location.latitude &&
                                                                        location.longitude &&
                                                                        typeof location.latitude === "number" &&
                                                                        typeof location.longitude === "number" && (
                                                                            <div className="location-detail-row">
                                                                                <Icon
                                                                                    path={mdiMapMarker}
                                                                                    size={0.7}
                                                                                    className="location-icon"
                                                                                />
                                                                                <span>
                                                                                    {location.latitude.toFixed(6)},{" "}
                                                                                    {location.longitude.toFixed(6)}
                                                                                </span>
                                                                            </div>
                                                                        )}
                                                                    {/* Venue Hire Pricing Display */}
                                                                    {location.venueHire &&
                                                                        location.venueHire.enabled && (
                                                                            <div className="location-detail-row venue-hire-info">
                                                                                <span className="venue-hire-label">
                                                                                    Venue Hire:
                                                                                </span>
                                                                                <span className="venue-hire-amount">
                                                                                    {formatCurrency(
                                                                                        location.venueHire.amount,
                                                                                        location.venueHire.currency
                                                                                    )}{" "}
                                                                                    (
                                                                                    {location.venueHire.type ===
                                                                                    "hourly"
                                                                                        ? "Per Hour"
                                                                                        : "Per Session"}
                                                                                    )
                                                                                </span>
                                                                            </div>
                                                                        )}
                                                                </div>

                                                                <div className="location-card-actions">
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleEditLocation(location)}
                                                                        className="btn-small btn-turquoise">
                                                                        <Icon path={mdiPencil} size={0.6} />
                                                                        Edit
                                                                    </button>
                                                                    {!location.isDefault && (
                                                                        <button
                                                                            type="button"
                                                                            onClick={() =>
                                                                                handleSetDefaultLocation(location.id)
                                                                            }
                                                                            className="btn-small btn-purple">
                                                                            <Icon path={mdiStar} size={0.6} />
                                                                            Set Default
                                                                        </button>
                                                                    )}
                                                                    <button
                                                                        type="button"
                                                                        onClick={() =>
                                                                            handleToggleLocationStatus(
                                                                                location.id,
                                                                                !location.isActive
                                                                            )
                                                                        }
                                                                        className="btn-small btn-gray">
                                                                        <Icon
                                                                            path={
                                                                                location.isActive ? mdiEyeOff : mdiEye
                                                                            }
                                                                            size={0.6}
                                                                        />
                                                                        {location.isActive ? "Deactivate" : "Activate"}
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() =>
                                                                            handleDeleteLocation(location.id)
                                                                        }
                                                                        className="btn-small btn-danger">
                                                                        <Icon path={mdiTrashCan} size={0.6} />
                                                                        Delete
                                                                    </button>
                                                                </div>
                                                            </>
                                                        )}
                                                    </div>
                                                );
                                            };

                                            return (
                                                <div className="form-section">
                                                    <div
                                                        style={{
                                                            display: "flex",
                                                            justifyContent: "space-between",
                                                            alignItems: "center",
                                                            marginBottom: "0.5rem",
                                                            gap: "12px",
                                                            flexWrap: "wrap",
                                                        }}>
                                                        <h2 className="section-title" style={{ marginBottom: 0 }}>
                                                            Teaching Locations
                                                        </h2>
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                setEditingLocation(null);
                                                                setShowLocationPicker(true);
                                                            }}
                                                            className="btn-turquoise btn-small">
                                                            <span
                                                                style={{
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    gap: "6px",
                                                                }}>
                                                                <Icon path={mdiPlus} size={0.8} />
                                                                Add Teaching Location
                                                            </span>
                                                        </button>
                                                    </div>
                                                    <p className="section-description">
                                                        Add your preferred teaching locations. Students will see these
                                                        when booking lessons with you.
                                                    </p>

                                                    <div className="location-cards-container">
                                                        {allLocations.length === 0 ? (
                                                            <div className="no-locations">
                                                                <p>No teaching locations added yet.</p>
                                                                <p>
                                                                    Add your first location to help students find where
                                                                    you teach.
                                                                </p>
                                                            </div>
                                                        ) : (
                                                            <>
                                                                <div className="location-group">
                                                                    <h3 className="location-group-title">
                                                                        <Icon
                                                                            path={mdiHomeOutline}
                                                                            size={0.8}
                                                                            style={{
                                                                                marginRight: "6px",
                                                                                verticalAlign: "middle",
                                                                            }}
                                                                        />
                                                                        Home Teaching Locations
                                                                        <span className="location-group-count">
                                                                            ({homeLocations.length})
                                                                        </span>
                                                                    </h3>
                                                                    {homeLocations.length === 0 ? (
                                                                        <p
                                                                            className="form-hint"
                                                                            style={{ marginTop: "8px" }}>
                                                                            No home locations yet.
                                                                        </p>
                                                                    ) : (
                                                                        homeLocations.map(renderLocationCard)
                                                                    )}
                                                                </div>

                                                                <div
                                                                    className="location-group"
                                                                    style={{ marginTop: "24px" }}>
                                                                    <h3 className="location-group-title">
                                                                        <Icon
                                                                            path={mdiAirplaneTakeoff}
                                                                            size={0.8}
                                                                            style={{
                                                                                marginRight: "6px",
                                                                                verticalAlign: "middle",
                                                                            }}
                                                                        />
                                                                        Travel Locations
                                                                        <span className="location-group-count">
                                                                            ({travelLocations.length})
                                                                        </span>
                                                                    </h3>
                                                                    {travelLocations.length === 0 ? (
                                                                        <p
                                                                            className="form-hint"
                                                                            style={{ marginTop: "8px" }}>
                                                                            No travel locations yet. Tick "This is a
                                                                            travel location" when adding a new location
                                                                            to categorize it as travel.
                                                                        </p>
                                                                    ) : (
                                                                        travelLocations.map(renderLocationCard)
                                                                    )}
                                                                </div>
                                                            </>
                                                        )}
                                                    </div>

                                                    <div style={{ marginTop: "16px" }}>
                                                        <button
                                                            type="button"
                                                            className="btn-save"
                                                            disabled={isSaving}
                                                            onClick={e => handleSubmit(e, "locations")}>
                                                            {isSaving ? "Saving..." : "Save Profile Details"}
                                                        </button>
                                                    </div>
                                                </div>
                                            );
                                        })()}
                                    </div>
                                )}
                            </div>
                        </div>
                    </form>
                </div>
            </div>

            {/* Location Picker Modal */}
            {showLocationPicker && (
                <TeachingLocationModal
                    isOpen={showLocationPicker}
                    onClose={() => {
                        setShowLocationPicker(false);
                        setEditingLocation(null);
                    }}
                    onSave={handleLocationSave}
                    initialData={editingLocation}
                    instructorHourlyRate={{
                        amount: parseFloat(profileData.hourlyRate) || 0,
                        currency: orgCurrency,
                    }}
                />
            )}

            {/* Delete Location Confirmation Modal */}
            <ConfirmationModal
                isOpen={showDeleteLocationModal}
                title="Delete Location?"
                message="Are you sure you want to delete this location? This action cannot be undone."
                confirmText="Delete"
                cancelText="Cancel"
                onConfirm={confirmDeleteLocation}
                onCancel={() => {
                    setShowDeleteLocationModal(false);
                    setLocationToDelete(null);
                }}
                variant="danger"
            />
        </div>
    );
};

export default InstructorProfilePage;
